import React, { useState, useCallback, useEffect } from "react";
import { Dialog } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Header } from "./Header";
import { AspectRatioSelector } from "./AspectRatioSelector";
import { OrientationToggle } from "./OrientationToggle";
import { CropperSection } from "./CropperSection";
import { getCroppedImg, getComputedAspect } from "./CropperHelper";
import { Container, Sidebar } from "./ModalCrop.style";
import { AssetList } from "../CarouselCrop/AssetList";
import { ASPECT_RATIO_LABEL } from "src/scenes/Noticeboard/constants";

// Sets screen width and height for mobile view,
const useStyles = makeStyles((theme) => ({
    paper: {
        width: 1425,
        height: 885,
        borderRadius: 15,
        overflow: "hidden",
        [theme.breakpoints.down("sm")]: {
            height: "100vh",
            width: "100%",
            maxWidth: "100%",
            borderRadius: 0,
        },
    },
}));

const pickAllowedAspectRatio = (aspectRatios, standardAspectRatio) => {
    return aspectRatios.find(data => data.standardName === standardAspectRatio) || aspectRatios[0];
};

const inferOrientation = standardAspectRatio => {
    switch (standardAspectRatio) {
    case ASPECT_RATIO_LABEL.WIDESCREEN:
    case ASPECT_RATIO_LABEL.WIDE_5_4:
        return "landscape";
    default:
        return "portrait";
    }
};

export default function ImageCrop({
    imageSrc,
    isOpen,
    onClose,
    onCropComplete,
    aspectRatios,
    allowedOrientations,
    returnAspectRationResult = false,
    carouselAssets,
    setCarouselAssets,
    carouselCurrentAsset,
    setCarouselCurrentAsset,
    croppedAssets,
    lockedAspectRatio,
}) {
    const classes = useStyles();
    // State to track cropping position and zoom level
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [cropBoxSize, setCropBoxSize] = useState({ width: 0, height: 0 });

    // Internal state for image source and aspect ratio
    const [imageSrcInternal, setImageSrcInternal] = useState(imageSrc || "");
    const [baseAspect, setBaseAspect] = useState(lockedAspectRatio ? pickAllowedAspectRatio(aspectRatios, lockedAspectRatio) : aspectRatios[0]);

    // Default orientation based on first available allowedOrientations position
    const [orientation, setOrientation] = useState(lockedAspectRatio ? inferOrientation(lockedAspectRatio) : allowedOrientations[0]);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

    // Function to calculate the computed aspect ratio based on the selected baseAspect and orientation
    const computedAspect = getComputedAspect(baseAspect, orientation);

    // Condition to check if the orientation toggle should be shown
    const showOrientationToggle =
        allowedOrientations.includes("landscape") && allowedOrientations.includes("portrait");

    // Update internal image source when imageSrc prop changes
    useEffect(() => {
        setImageSrcInternal(imageSrc || "");
    }, [imageSrc]);

    // Set default base aspect and orientation on mount or change
    useEffect(() => {
        if (allowedOrientations.includes("circle")) {
            setBaseAspect({ width: 1, height: 1, label: "1:1 (Circle)" });
            setOrientation("landscape");
        } else if (lockedAspectRatio) {
            const matchingAspectRatio = pickAllowedAspectRatio(aspectRatios, lockedAspectRatio);
            if (matchingAspectRatio) {
                setBaseAspect(matchingAspectRatio);
                setOrientation(inferOrientation(lockedAspectRatio));
            }
        } else {
            setBaseAspect(aspectRatios[0]);

            if (allowedOrientations.length === 1) {
                setOrientation(allowedOrientations[0]);
            } else if (allowedOrientations.includes("landscape")) {
                setOrientation("landscape");
            } else if (allowedOrientations.includes("portrait")) {
                setOrientation("portrait");
            }
        }
    }, [allowedOrientations, aspectRatios, lockedAspectRatio]);

    // Ensure selected aspect is compatible with current orientation
    useEffect(() => {
        if (!baseAspect || !orientation || !aspectRatios) return;

        const filteredRatios = aspectRatios.filter((aspect) => {
            if (orientation === "landscape") return aspect.width >= aspect.height;
            if (orientation === "portrait") return aspect.height > aspect.width;
            return true;
        });

        const isValid = filteredRatios.some(
            (r) => r.width === baseAspect.width && r.height === baseAspect.height
        );

        if (!isValid && filteredRatios.length > 0) {
            setBaseAspect(filteredRatios[0]);
        }
    }, [orientation, aspectRatios]);
    const handleCropSizeChange = useCallback(({ width, height }) => {
        setCropBoxSize(currentSize => (
            currentSize.width === width && currentSize.height === height
                ? currentSize
                : { width, height }
        ));
    }, []);

    // Function to handle cancel action when the modal is closed
    const handleCancel = () => {
        setImageSrcInternal("");
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        onClose(true);
    };

    // useCallback to handle crop completion, and store cropped pixel data
    const handleCropComplete = useCallback((_, croppedPixels) => {
        setCroppedAreaPixels(croppedPixels);
    }, []);

    // Function to handle the actual crop operation
    const handleCrop = async () => {
        const croppedBlob = await getCroppedImg(imageSrcInternal, croppedAreaPixels);
        if (returnAspectRationResult) {
            onCropComplete(
                croppedBlob,
                croppedAreaPixels.width,
                croppedAreaPixels.height,
                baseAspect.standardName
            );
        } else {
            onCropComplete(croppedBlob, croppedAreaPixels.width, croppedAreaPixels.height);
        }
    };

    useEffect(() => {
        if (lockedAspectRatio) {
            const matchingAspectRatio = pickAllowedAspectRatio(aspectRatios, lockedAspectRatio);
            if (matchingAspectRatio) {
                setOrientation(inferOrientation(lockedAspectRatio));
                setBaseAspect(matchingAspectRatio);
            }
            return;
        }
    }, [lockedAspectRatio, orientation, aspectRatios]);

    return (
        <Dialog
            open={isOpen}
            maxWidth={false}
            onClose={handleCancel}
            PaperProps={{ className: classes.paper }}
        >
            {/* Header contains the text header and cancel save buttons */}
            <Header onCancel={handleCancel} onSave={handleCrop} />

            {/* Sidebar for the aspect ratio selector and orientation toggle */}
            <Container>
                <Sidebar>
                    {/* Aspect ratio selection component */}
                    <div
                        style={{
                            pointerEvents: lockedAspectRatio ? "none" : "all",
                            opacity: lockedAspectRatio ? 0.5 : 1
                        }}
                    >
                        <AspectRatioSelector
                            aspectRatios={aspectRatios}
                            baseAspect={baseAspect}
                            setBaseAspect={setBaseAspect}
                            setOrientation={showOrientationToggle ? setOrientation : undefined}
                            orientation={orientation}
                        />
                    </div>
                    {/* Show orientation toggle when (9:16 or 4:5 aspect ratio) is selected */}
                    {showOrientationToggle && (
                        <OrientationToggle
                            orientation={orientation}
                            setOrientation={setOrientation}
                            allowedOrientations={allowedOrientations}
                            lockedAspectRatio={lockedAspectRatio}
                        />
                    )}

                    {carouselAssets && (
                        <AssetList
                            carouselAssets={carouselAssets}
                            setCarouselAssets={setCarouselAssets}
                            carouselCurrentAsset={carouselCurrentAsset}
                            setCarouselCurrentAsset={setCarouselCurrentAsset}
                            croppedAssets={croppedAssets}
                        />
                    )}
                </Sidebar>

                {/* Cropper section that handles the actual cropping process */}
                <CropperSection
                    imageSrcInternal={imageSrcInternal}
                    crop={crop}
                    zoom={zoom}
                    setCrop={setCrop}
                    setZoom={setZoom}
                    computedAspect={computedAspect}
                    handleCropComplete={handleCropComplete}
                    handleCropSizeChange={handleCropSizeChange}
                    cropBoxSize={cropBoxSize}
                    setImageSrcInternal={setImageSrcInternal}
                    isCircle={allowedOrientations.includes("circle")}
                />
            </Container>
        </Dialog>
    );
}
